import { useEffect, useRef, useState } from 'react'
import { css } from '@firebolt-dev/css'
import { Link, useLocation } from 'wouter'

import { useAuth } from './useAuth'
import { unpackURL } from '../shared/urls'
import { Logo } from './Logo'
import { CircleUserIcon, LoaderIcon, UserIcon, XIcon } from 'lucide-react'

let cached

export function Home() {
  const auth = useAuth()
  const [location, navigate] = useLocation()
  const [secret, setSecret] = useState(false)
  const [chats, setChats] = useState(cached)
  const [n, setN] = useState(0)
  const [account, setAccount] = useState(false)

  const punch = useSecret(() => setSecret(true))

  useEffect(() => {
    async function init() {
      const chats = await auth.get('/chats')
      setChats(chats)
      cached = chats
    }
    init()
  }, [n])

  const create = async () => {
    const chat = await auth.post('/chats')
    navigate(`/${chat.sim.id}`)
  }

  return (
    <div
      css={css`
        min-height: 100dvh;
        display: flex;
        flex-direction: column;
        .gmu {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: #f63939;
          z-index: 999;
        }
        .main {
          height: calc(100dvh - 120px);
          display: flex;
          flex-direction: column;
        }
        .header {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          height: 60px;
          padding: 0 10px;
          display: flex;
          align-items: center;
          background: #16171a;
          &-logo {
            width: 40px;
            height: 40px;
            display: flex;
            align-items: center;
            justify-content: center;
            svg {
              width: 24px;
              height: auto;
            }
          }
          &-logotext {
            font-size: 19px;
            font-weight: 500;
          }
          &-gap {
            flex: 1;
          }
          &-btn {
            width: 40px;
            height: 40px;
            display: flex;
            /* background: #2a2c33; */
            align-items: center;
            justify-content: center;
            cursor: pointer;
          }
        }
        .mid {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px 0; // 60=header size
        }
        .pfps {
          width: 100%;
          max-width: 490px;
          margin: 0 0 0px;
        }
        .tagline {
          text-align: center;
          font-size: 31px;
          font-weight: 500;
          margin: 0 0 10px;
        }
        .subtext {
          max-width: 480px;
          font-size: 16px;
          line-height: 1.4;
          text-align: center;
          color: rgba(255, 255, 255, 0.6);
          margin: 0 0 20px;
        }
        .logo {
          width: 100px;
          height: 100px;
          margin: 0 0 40px;
          svg {
            max-width: 100%;
            height: auto;
          }
        }
        .create {
          padding: 0 30px;
          height: 50px;
          background: #2f3139;
          border-radius: 25px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 500;
          &:hover {
            cursor: pointer;
            background: #494d57;
          }
        }
        .sims {
          padding: 0 20px 20px;
          display: flex;
          justify-content: center;
          flex-wrap: wrap;
          gap: 20px;
        }
        .sim {
          display: flex;
          flex-direction: column;
          align-items: center;
          &-image {
            width: 70px;
            height: 70px;
            border-radius: 50%;
            background-color: #494d57;
            background-position: center;
            background-size: cover;
            margin: 0 0 6px;
            box-shadow: 0 0 0 rgba(0, 0, 0, 0);
            transition: all 0.15s ease-out;
          }
          &-name {
            font-size: 15px;
            font-weight: 500;
          }
          &:hover {
            cursor: pointer;
            .sim-image {
              transform: scale(1.15);
              box-shadow: 0 5px 10px rgba(0, 0, 0, 0.3);
            }
          }
        }
      `}
    >
      {auth.user.gmu && <div className='gmu' />}
      <div className='main'>
        <div className='header'>
          <div className='header-logo' onClick={punch}>
            <Logo />
          </div>
          <div className='header-logotext'>Simber</div>
          <div className='header-gap' />
          <div className='header-btn' onClick={() => setAccount(true)}>
            <CircleUserIcon size={22} />
          </div>
        </div>
        <div className='mid'>
          {/* <img className='pfps' src='/pfps.png' /> */}
          <div className='tagline'>Shape Someone New</div>
          <div className='subtext'>
            Sims learn, grow and respond to everything you say. They are always here and live forever.
          </div>
          <div className='create' onClick={create}>
            <span>Create Sim</span>
          </div>
        </div>
      </div>
      <div className='sims'>
        {!chats && (
          <div className='sims-loading'>
            <Loading />
          </div>
        )}
        {chats?.map(chat => (
          <Link key={chat.id} to={`/${chat.sim.id}`}>
            <div className='sim'>
              <div
                className='sim-image'
                css={css`
                  background-image: ${chat.sim.image ? 'url(' + unpackURL(chat.sim.image) + ')' : ''};
                `}
              />
              <div className='sim-name'>{chat.sim.name || 'Sim'}</div>
            </div>
          </Link>
        ))}
      </div>
      {secret && (
        <Secret
          onClose={reload => {
            setSecret(false)
            if (reload) setN(n + 1)
          }}
        />
      )}
      {account && <Account onClose={() => setAccount(false)} />}
    </div>
  )
}

function Loading() {
  return (
    <div
      css={css`
        margin-top: 30px;
        color: rgba(255, 255, 255, 0.1);
        svg {
          animation: spin 2s linear infinite;
          @keyframes spin {
            from {
              transform: rotate(0turn);
            }
            to {
              transform: rotate(1turn);
            }
          }
        }
      `}
    >
      <LoaderIcon size={24} />
    </div>
  )
}

function useSecret(fn) {
  const count = useRef(0)
  return () => {
    count.current++
    if (count.current === 5) {
      count.current = 0
      fn()
    }
  }
}

function Secret({ onClose }) {
  const auth = useAuth()
  const [users, setUsers] = useState([])
  useEffect(() => {
    const load = async () => {
      const users = await auth.get('/users')
      setUsers(users)
    }
    load()
  }, [])
  const select = user => {
    auth.setGMU(user.id)
    onClose(true)
  }
  return (
    <div
      css={css`
        position: fixed;
        inset: 0;
        background: black;
      `}
    >
      <div onClick={() => onClose(false)}>close</div>
      {users.map(user => (
        <div key={user.id} onClick={() => select(user)}>
          {user.id}
        </div>
      ))}
    </div>
  )
}

function Account({ onClose }) {
  const outerRef = useRef()
  const auth = useAuth()
  const [email, setEmail] = useState(auth.user.email || '')
  const [name, setName] = useState(auth.user.name)
  const [mode, setMode] = useState(auth.user.email ? 'account' : 'guest')
  useEffect(() => {
    setTimeout(() => {
      outerRef.current.classList.add('visible')
    }, 10)
  }, [])
  return (
    <div
      ref={outerRef}
      className='acc'
      css={css`
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        height: 100dvh;
        padding: 20px;
        background: rgba(0, 0, 0, 0.2);
        backdrop-filter: blur(5px);
        display: flex;
        align-items: center;
        justify-content: center;
        .acc-modal {
          width: 340px;
          box-shadow: 0 2px 20px rgba(0, 0, 0, 0.6);
          background: #191b1e;
          border: 1px solid #25272c;
          border-radius: 20px;
          padding: 40px;
          position: relative;
          transition: all 0.15s ease-out;
          opacity: 0;
          transform: translate(0px, 20px);
        }
        .acc-title {
          text-align: center;
          font-size: 21px;
          font-weight: 500;
          margin: 0 0 20px;
        }
        .acc-text {
          text-align: center;
          color: rgba(255, 255, 255, 0.6);
          line-height: 1.4;
          margin: 0 0 40px;
        }
        .acc-input {
          display: flex;
          align-items: center;
          padding: 0 15px;
          height: 40px;
          border-radius: 20px;
          background: #2a2c33;
          margin: 0 0 10px;
          input {
            width: 100%;
          }
        }
        .acc-btn {
          height: 40px;
          border-radius: 20px;
          background: #373a43;
          display: flex;
          align-items: center;
          justify-content: center;
          /* font-weight: 500; */
          margin: 0 0 20px;
          &:hover {
            cursor: pointer;
            background: #40444eff;
          }
        }
        .acc-sub {
          text-align: center;
          font-size: 15px;
          a {
            cursor: pointer;
            color: #874fff;
          }
        }
        .acc-url {
          border-radius: 20px;
          background: #2a2c33;
          height: 40px;
          padding: 0 16px;
          display: flex;
          align-items: center;
          span {
            flex: 1;
            white-space: nowrap;
            text-overflow: ellipsis;
            overflow: hidden;
          }
          svg {
            margin-left: 10px;
          }
          &:hover {
            cursor: pointer;
            background: #34363f;
          }
        }
        .acc-close {
          position: absolute;
          top: 10px;
          right: 10px;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #3a3d46;
          &:hover {
            cursor: pointer;
            color: white;
          }
        }
        &.visible {
          .acc-modal {
            opacity: 1;
            transform: translate(0px, 0px);
          }
        }
      `}
      onClick={e => {
        if (e.fromModal) return
        onClose()
      }}
    >
      <div className='acc-modal' onClick={e => (e.fromModal = true)}>
        {mode === 'guest' && (
          <>
            <div className='acc-title'>Guest Account</div>
            <div className='acc-text'>
              Your sims and chats are saved on this device only. Add an email to keep them forever.
            </div>
            <label className='acc-input'>
              <input placeholder='your@email.com' type='text' value={email} onChange={e => setEmail(e.target.value)} />
            </label>
            <div className='acc-btn'>
              <span>Save My Account</span>
            </div>
            <div className='acc-sub'>
              Already have an account? <a>Sign in</a>
            </div>
          </>
        )}
        <div className='acc-close' onClick={onClose}>
          <XIcon size={20} />
        </div>
      </div>
    </div>
  )
}
